import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useEscapeKey } from '../hooks/useEscapeKey';
const TOOLTIP_GUTTER = 8;
function getPositionStyle(side, trigger, tooltip) {
    const maxLeft = window.innerWidth - tooltip.width - TOOLTIP_GUTTER;
    const maxTop = window.innerHeight - tooltip.height - TOOLTIP_GUTTER;
    const clampLeft = (left) => Math.max(TOOLTIP_GUTTER, Math.min(left, maxLeft));
    const clampTop = (top) => Math.max(TOOLTIP_GUTTER, Math.min(top, maxTop));
    switch (side) {
        case 'top':
            return {
                left: clampLeft(trigger.left + (trigger.width - tooltip.width) / 2),
                top: clampTop(trigger.top - tooltip.height - TOOLTIP_GUTTER),
            };
        case 'bottom':
            return {
                left: clampLeft(trigger.left + (trigger.width - tooltip.width) / 2),
                top: clampTop(trigger.bottom + TOOLTIP_GUTTER),
            };
        case 'left':
            return {
                left: clampLeft(trigger.left - tooltip.width - TOOLTIP_GUTTER),
                top: clampTop(trigger.top + (trigger.height - tooltip.height) / 2),
            };
        case 'right':
            return {
                left: clampLeft(trigger.right + TOOLTIP_GUTTER),
                top: clampTop(trigger.top + (trigger.height - tooltip.height) / 2),
            };
    }
}
export function Tooltip({ content, children, side = 'top', delay = 400 }) {
    const [isVisible, setIsVisible] = useState(false);
    const [tooltipStyle, setTooltipStyle] = useState({});
    const timerRef = useRef(null);
    const triggerRef = useRef(null);
    const tooltipRef = useRef(null);
    const tooltipId = useId();
    useEscapeKey(() => setIsVisible(false), isVisible);
    const updateTooltipPosition = useCallback(() => {
        if (!triggerRef.current || !tooltipRef.current)
            return;
        setTooltipStyle(getPositionStyle(side, triggerRef.current.getBoundingClientRect(), tooltipRef.current.getBoundingClientRect()));
    }, [side]);
    useLayoutEffect(() => {
        if (!isVisible)
            return;
        updateTooltipPosition();
        window.addEventListener('resize', updateTooltipPosition);
        window.addEventListener('scroll', updateTooltipPosition, true);
        return () => {
            window.removeEventListener('resize', updateTooltipPosition);
            window.removeEventListener('scroll', updateTooltipPosition, true);
        };
    }, [isVisible, updateTooltipPosition]);
    function showAfterDelay() {
        timerRef.current = setTimeout(() => setIsVisible(true), delay);
    }
    function showImmediate() {
        if (timerRef.current)
            clearTimeout(timerRef.current);
        setIsVisible(true);
    }
    function hide() {
        if (timerRef.current)
            clearTimeout(timerRef.current);
        setIsVisible(false);
    }
    return (_jsxs("span", { className: "relative inline-flex", onMouseEnter: showAfterDelay, onMouseLeave: hide, onFocus: showImmediate, onBlur: hide, children: [_jsx("span", { ref: triggerRef, "aria-describedby": isVisible ? tooltipId : undefined, children: children }), isVisible && typeof document !== 'undefined' && createPortal(_jsx("span", { ref: tooltipRef, id: tooltipId, role: "tooltip", className: "fixed py-1 px-2 glass-surface bg-[var(--sam-tooltip-bg)] rounded-sm shadow-tooltip text-fg-primary max-w-[200px] z-dropdown pointer-events-none whitespace-normal", style: {
                    fontSize: 'var(--sam-type-caption-size)',
                    lineHeight: 'var(--sam-type-caption-line-height)',
                    ...tooltipStyle,
                }, children: content }), document.body)] }));
}
//# sourceMappingURL=Tooltip.js.map